import { useEffect, useState } from "react";
import { SRGBColorSpace, Texture, TextureLoader } from "three";

const MAX_CACHED = 3;
// Insertion-ordered Map doubles as the LRU eviction queue.
const cache = new Map<string, Texture>();
const loader = new TextureLoader();

function putCached(url: string, texture: Texture): void {
  cache.set(url, texture);
  while (cache.size > MAX_CACHED) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.get(oldestKey)?.dispose();
    cache.delete(oldestKey);
  }
}

function finalizeTexture(texture: Texture): Texture {
  texture.colorSpace = SRGBColorSpace;
  texture.generateMipmaps = true;
  return texture;
}

// Imperative texture loader — deliberately NOT drei's useTexture, which
// suspends. This codebase has already had two on-device incidents where a
// stuck Suspense blanked a whole panel with zero visible error (see
// fonts.ts's kill-switch note); an illustration failing/being slow to load
// must never risk that again. Returns null while loading or on error — the
// panel simply shows no image yet, nothing else in the scene is affected.
// Bounded LRU (3 entries) so paging through the 12 onboarding screens
// doesn't accumulate ~45MB of orphaned VRAM (drei's useTexture cache never
// releases at all).
export function usePanelTexture(url: string | null): Texture | null {
  const [texture, setTexture] = useState<Texture | null>(() =>
    url ? (cache.get(url) ?? null) : null,
  );

  useEffect(() => {
    if (!url) {
      setTexture(null);
      return;
    }
    const cached = cache.get(url);
    if (cached) {
      setTexture(cached);
      return;
    }

    let cancelled = false;
    loader.load(
      url,
      (loaded) => {
        putCached(url, finalizeTexture(loaded));
        if (!cancelled) setTexture(loaded);
      },
      undefined,
      () => {
        if (!cancelled) setTexture(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [url]);

  return texture;
}

// Warms the cache for the next screen's image ahead of time — called from
// TeachLessonPanel3D when tutorialLineIndex changes, so navigating forward
// feels instant instead of popping in.
export function prefetchPanelImage(url: string): void {
  if (cache.has(url)) return;
  loader.load(url, (loaded) => putCached(url, finalizeTexture(loaded)));
}
